import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { xtreamApi } from '../services/xtreamApi';
import LoadingSpinner from '../components/Common/LoadingSpinner';
import FavoriteButton from '../components/Common/FavoriteButton';
import { Heart, Film, MonitorPlay, Tv, Play, Star } from 'lucide-react';

const TABS = [
  { key: 'all', label: 'All', icon: Heart },
  { key: 'movie', label: 'Movies', icon: Film },
  { key: 'series', label: 'Series', icon: MonitorPlay },
  { key: 'live', label: 'Live TV', icon: Tv },
];

const MyListPage = () => {
  const [favorites, setFavorites] = useState([]);
  const [activeTab, setActiveTab] = useState('all');
  const [isLoading, setIsLoading] = useState(true);

  const fetchFavorites = async () => {
    try {
      setIsLoading(true);
      const data = await xtreamApi.getFavorites(activeTab === 'all' ? null : activeTab);
      setFavorites(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Error fetching favorites:', err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchFavorites();
  }, [activeTab]);

  return (
    <div data-testid="mylist-page" className="min-h-screen bg-[#0a0a0b] pt-20 px-4 sm:px-8 lg:px-16">
      <div className="max-w-[1800px] mx-auto">
        <div className="flex items-center gap-3 mb-6">
          <Heart className="w-7 h-7 text-red-500 fill-red-500" />
          <h1 className="text-3xl font-bold text-white">My List</h1>
        </div>

        {/* Tabs */}
        <div data-testid="mylist-tabs" className="flex gap-1 mb-8 bg-[#1a1a1d] rounded-lg p-1 w-fit">
          {TABS.map(({ key, label, icon: Icon }) => (
            <button
              key={key}
              data-testid={`mylist-tab-${key}`}
              onClick={() => setActiveTab(key)}
              className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-all ${
                activeTab === key ? 'text-white' : 'text-gray-400 hover:text-white hover:bg-gray-700'
              }`}
              style={activeTab === key ? {backgroundColor:'var(--accent)'} : {}}
            >
              <Icon className="w-4 h-4" />
              {label}
            </button>
          ))}
        </div>

        {isLoading ? (
          <div className="flex justify-center py-20">
            <LoadingSpinner text="Loading favorites..." />
          </div>
        ) : favorites.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20">
            <Heart className="w-16 h-16 text-gray-700 mb-4" />
            <p className="text-gray-500 text-lg">No favorites yet</p>
            <p className="text-gray-600 text-sm mt-2">Browse content and tap the heart to save items here</p>
          </div>
        ) : (
          <div data-testid="mylist-grid" className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 2xl:grid-cols-7 gap-4">
            {favorites.map((item) => (
              <Link
                key={`${item.content_type}-${item.content_id}`}
                to={`/${item.content_type}/${item.content_id}`}
                className="group relative block rounded-lg overflow-hidden bg-gray-900"
                data-testid={`mylist-item-${item.content_id}`}
              >
                <div className="aspect-[2/3] relative overflow-hidden">
                  <img
                    src={item.poster}
                    alt={item.name}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                    loading="lazy"
                    onError={(e) => { e.target.src = 'https://via.placeholder.com/200x300?text=No+Poster'; }}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                  
                  <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    <div className="w-14 h-14 rounded-full bg-white/90 flex items-center justify-center">
                      <Play className="w-6 h-6 text-black fill-black ml-1" />
                    </div>
                  </div>

                  {item.rating && item.rating !== '0' && (
                    <div className="absolute top-2 right-2 flex items-center gap-1 px-2 py-1 bg-black/70 rounded-md">
                      <Star className="w-3 h-3 text-yellow-400 fill-yellow-400" />
                      <span className="text-xs text-white font-medium">{item.rating}</span>
                    </div>
                  )}

                  {/* Favorite button (to unfavorite) */}
                  <div className="absolute top-2 left-2 z-10">
                    <FavoriteButton
                      contentId={item.content_id}
                      contentType={item.content_type}
                      name={item.name}
                      poster={item.poster}
                      rating={item.rating}
                      className="p-1.5 bg-black/60 rounded-full backdrop-blur-sm hover:bg-black/80"
                    />
                  </div>

                  {/* Type badge */}
                  <div className="absolute bottom-2 left-2">
                    <span style={{backgroundColor:'var(--accent)',opacity:0.8}} className="px-2 py-0.5 text-white text-[10px] font-medium rounded uppercase">
                      {item.content_type}
                    </span>
                  </div>
                </div>

                <div className="p-2">
                  <h3 className="text-white text-sm font-medium line-clamp-1">{item.name}</h3>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default MyListPage;
